import React from 'react';
import {
  ListItem as MuiListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Avatar,
  Typography,
  Box,
  
  IconButton,
  Chip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { colorPrimary } from '../../Styles/Colors';
import Checkbox from './Checkbox';
import Switch from './Switch';

const ListItem = ({
  // Content props
  primaryText = "List Item",
  secondaryText,
  avatarText,
  avatarSrc,
  avatarIcon,
  
  // Styling props
  variant = "default", // default, avatar, icon, two-line, three-line
  size = "medium", // small, medium, large
  divider = false,
  selected = false,
  disabled = false,
  
  // Interactive props
  onClick,
  onSecondaryAction,
  showCheckbox = false,
  checkboxChecked = false,
  onCheckboxChange,
  showSwitch = false,
  switchChecked = false,
  onSwitchChange,
  showMoreOptions = false,
  
  // Additional content
  rightContent,
  badge,
  status,
  
  // Custom styling
  sx = {}
}) => {
  
  const renderAvatar = () => {
    if (avatarSrc) {
      return <Avatar src={avatarSrc} sx={{ width: 40, height: 40 }} />;
    }
    if (avatarIcon) {
      return (
        <Avatar sx={{ width: 40, height: 40, bgcolor: colorPrimary }}>
          {avatarIcon}
        </Avatar>
      );
    }
    if (avatarText) {
      return (
        <Avatar sx={{ width: 40, height: 40, bgcolor: colorPrimary }}>
          {avatarText}
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.400' }}>
        <PersonIcon />
      </Avatar>
    );
  };

  const renderRightContent = () => {
    if (showCheckbox) {
      return (
        <Checkbox
          checked={checkboxChecked}
          onChange={onCheckboxChange}
          size={size === 'small' ? 'small' : 'medium'}
        />
      );
    }
    
    if (showSwitch) {
      return (
        <Switch
          checked={switchChecked}
          onChange={onSwitchChange}
          size={size === 'small' ? 'small' : 'medium'}
        />
      );
    }
    
    if (showMoreOptions) {
      return (
        <IconButton onClick={onSecondaryAction} size="small">
          <MoreVertIcon />
        </IconButton>
      );
    }
    
    if (rightContent) {
      return rightContent;
    }
    
    return null;
  };

  const getItemHeight = () => {
    switch (size) {
      case 'small': return secondaryText ? 64 : 48;
      case 'large': return secondaryText ? 88 : 72;
      default: return secondaryText ? 72 : 56;
    }
  };

  const Component = onClick ? ListItemButton : MuiListItem;

  return (
    <Component
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      divider={divider}
      sx={{
        minHeight: getItemHeight(),
        py: size === 'small' ? 1 : size === 'large' ? 2 : 1.5,
        px: 2,
        position: 'relative',
        '&.Mui-selected': {
          bgcolor: 'primary.light',
          '&:hover': {
            bgcolor: 'primary.light',
          },
        },
        '&:hover': {
          bgcolor: onClick ? 'action.hover' : 'transparent',
        },
        transition: 'background-color 0.2s ease',
        ...sx
      }}
    >
   
      {(variant === 'avatar' || variant === 'icon' || avatarText || avatarSrc || avatarIcon) && (
        <ListItemAvatar>
          {renderAvatar()}
        </ListItemAvatar>
      )}
      
      
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant={size === 'small' ? 'body2' : 'body1'}
              sx={{
                fontWeight: selected ? 500 : 400,
                color: disabled ? 'text.disabled' : 'text.primary'
              }}
            >
              {primaryText}
            </Typography>
            
      
            {badge && (
              <Chip
                label={badge}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
            
          
            {status && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: status === 'online' ? 'success.main' : 
                           status === 'busy' ? 'error.main' : 
                           status === 'away' ? 'warning.main' : 'grey.400'
                }}
              />
            )}
          </Box>
        }
        secondary={secondaryText && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              fontSize: size === 'small' ? '0.75rem' : '0.875rem'
            }}
          >
            {secondaryText}
          </Typography>
        )}
      />
      
   
      {renderRightContent()}
    </Component>
  );
};

export default ListItem;
